import { Alert, DropdownCreatable } from '@designsforhealth/dfh-react-components'
import { Typography } from '@material-ui/core'
import React, { useCallback, useEffect, useMemo } from 'react'
import { NestedValue, SubmitHandler, useForm } from 'react-hook-form'
import styled from 'styled-components'

import useSubmitFormOnDirty from '../../hooks/useSubmitFormOnDirty'
import useUserCategory from '../../hooks/useUserCategory'
import {
  formatQuantityError,
  isOutOfStockError,
  isQuantityError,
  resolveItemQuantityConfig,
  selectOptionsToDisplay,
  validUpdateQuantityOptionsFor,
} from '../../lib/cart/item-control-options'
import { CartItem } from '../../lib/cart/types'
import { maxItemOrderQuantityByUserCategory } from '../../utils/itemTools'
import { CartItemAlert } from './CartItemAlert'
import UpdateCartItemErrorAlert from './UpdateCartItemErrorAlert'

const StyledErrorMessage = styled(Typography)`
  font-size: 0.75rem;
  margin-bottom: 10px;
  margin-top: -10px;
  color: ${({ theme }) => theme.colors.semantic.error};
`

const StyledDropdownCreatable = styled(DropdownCreatable)`
  width: 94px;
  max-width: 120px;
  margin: unset;
  .react-select__control {
    flex-wrap: nowrap;
  }

  .react-select__control::before {
    padding: 8px 0 8px 4px;
  }
`

export interface CartItemQuantityControlProps {
  label?: string
  className?: string
  disabled: boolean
  item: CartItem
  updateCartItem(props: { quantity?: number }): Promise<{ error?: Error }>
}

interface FormValues {
  quantity?: NestedValue<{ label: string; value: string }>
}

const CartItemQuantityControl: React.FC<CartItemQuantityControlProps> = ({
  className,
  disabled,
  item,
  updateCartItem,
  label = 'Qty:',
}) => {
  const { userCategory } = useUserCategory()

  const itemQuantityConfig = useMemo(
    () => resolveItemQuantityConfig(item.product.stock_configuration),
    [item.product.stock_configuration],
  )

  // collect set of valid quantities from which select options are produced and custom values validated
  const validQuantityOptionValues = useMemo(
    () =>
      validUpdateQuantityOptionsFor(itemQuantityConfig)(
        maxItemOrderQuantityByUserCategory(userCategory),
      ),
    [itemQuantityConfig, userCategory],
  )

  const quantityOptions = useMemo(
    () => selectOptionsToDisplay(validQuantityOptionValues),
    [validQuantityOptionValues],
  )

  const defaultQuantityOption = useMemo(
    () => selectOptionsToDisplay([item.quantity])[0],
    [item.quantity],
  )

  const { control, errors, formState, handleSubmit, reset, setError } = useForm<FormValues>({
    mode: 'onChange',
  })

  useEffect(() => {
    if (item.quantity < itemQuantityConfig.minQuantityAllowedInCart) {
      setError('quantity', {
        message: `Please select a new qty. The fewest you may purchase is ${itemQuantityConfig.minQuantityAllowedInCart}.`,
        type: 'validate',
      })
    } else if (item.quantity > itemQuantityConfig.maxQuantityAllowedInCart) {
      setError('quantity', {
        message: `Please select a new qty. The maximum you may purchase is ${itemQuantityConfig.maxQuantityAllowedInCart}.`,
        type: 'validate',
      })
    } else if (!validQuantityOptionValues.includes(item.quantity)) {
      setError('quantity', {
        message: `Please select a new qty. Current qty is no longer a valid option.`,
        type: 'validate',
      })
    }
  }, [
    item.quantity,
    itemQuantityConfig.maxQuantityAllowedInCart,
    itemQuantityConfig.minQuantityAllowedInCart,
    setError,
    validQuantityOptionValues,
  ])

  const onSubmit: SubmitHandler<FormValues> = useCallback(
    async ({ quantity }) => {
      if (!quantity) {
        return
      }

      const selectedQuantity = Number(quantity.value)

      if (Number.isNaN(selectedQuantity)) {
        setError('quantity', { message: 'Numeric value required', type: 'validate' })
        return
      }

      if (selectedQuantity === item.quantity) {
        return
      }

      const result = await updateCartItem({
        quantity: selectedQuantity,
      })

      if (result.error) {
        const isOOSError = isOutOfStockError(result.error.message)
        const isQtyError = isQuantityError(result.error.message)
        setError('quantity', {
          message: isQtyError
            ? formatQuantityError(result.error.message, 'Invalid Qty')
            : isOOSError
              ? 'This product is out of stock. Remove item to continue.'
              : result.error.message,
          type: isQtyError ? 'validate' : isOOSError ? 'out_of_stock' : 'server',
        })
      }
    },
    [item.quantity, setError, updateCartItem],
  )

  const { resetForm } = useSubmitFormOnDirty({
    defaultValues: useMemo(
      () => ({
        quantity: defaultQuantityOption,
      }),
      [defaultQuantityOption],
    ),
    formState,
    handleSubmit,
    onSubmit,
    reset,
  })

  return (
    <>
      <StyledDropdownCreatable
        className={className}
        control={control}
        name="quantity"
        options={quantityOptions}
        prompt={label}
        minMenuHeight={150}
        maxMenuHeight={150}
        menuPlacement="auto"
        captureMenuScroll={true}
        defaultValue={defaultQuantityOption}
        disabled={disabled}
        formatCreateLabel={(inputValue) => inputValue}
        isValidNewOption={(inputValue, _selectValue, selectOptions) => {
          if (selectOptions.some(({ value }) => value === inputValue)) {
            return false
          }
          const numberValue = Number(inputValue)
          return !Number.isNaN(numberValue) && validQuantityOptionValues.includes(numberValue)
        }}
      />

      <CartItemAlert cartItemId={item.id} />

      {errors.quantity &&
        (errors.quantity.type === 'server' ? (
          <UpdateCartItemErrorAlert onCancel={resetForm} onRetry={handleSubmit(onSubmit)} />
        ) : errors.quantity.type === 'out_of_stock' ? (
          <Alert type="error">{errors.quantity.message}</Alert>
        ) : (
          <StyledErrorMessage variant="body2">{errors.quantity.message}</StyledErrorMessage>
        ))}
    </>
  )
}

export default CartItemQuantityControl
